import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Users,
  BookOpen,
  Calendar,
  Clock,
  GraduationCap,
  TrendingUp,
  Bell,
  AlertCircle,
  Plus,
  CheckCircle,
  Info,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyTeacherProfile } from "../../services/teachers";
import { getAttendanceSummary } from "../../services/attendance";
import { getStudentNotices, Notice } from "../../services/notices";

const TeacherDashboard: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const navigate = useNavigate();

  // Fetch teacher profile
  const {
    data: teacherData,
    isLoading: teacherLoading,
    error: teacherError,
  } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  // Fetch notices
  const { data: noticesData, isLoading: noticesLoading } = useQuery({
    queryKey: ["teacher-notices"],
    queryFn: () => getStudentNotices(),
    enabled: !!user?.id,
    retry: false,
  });

  // Fetch attendance summary
  const { data: attendanceData } = useQuery({
    queryKey: ["teacher-attendance-summary"],
    queryFn: () =>
      getAttendanceSummary(new Date().toISOString().split("T")[0], "student"),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    enabled: !!user?.id,
    retry: false, // Don't retry on failure
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  if (teacherLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
          <p className="mt-4 text-slate-600 font-medium">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (teacherError) {
    return (
      <div className="p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center shadow-md">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-800 font-bold">
                Error Loading Teacher Data
              </h3>
              <p className="text-red-600 mt-1 font-medium">
                Unable to load your profile information. Please try refreshing
                the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const teacher = teacherData?.data;
  const allNotices = noticesData?.data || [];

  // Filter notices to show only those targeted at teachers and all users for dashboard
  const notices = allNotices.filter(
    (notice: Notice) =>
      notice.targetAudience === "all" || notice.targetAudience === "teachers"
  );

  // Helper functions for notices
  const getNoticeIcon = (type: string) => {
    switch (type) {
      case "urgent":
      case "important":
        return <AlertCircle className="h-5 w-5 text-white" />;
      case "academic":
        return <CheckCircle className="h-5 w-5 text-white" />;
      case "info":
      case "general":
        return <Info className="h-5 w-5 text-white" />;
      default:
        return <Bell className="h-5 w-5 text-white" />;
    }
  };

  const getNoticeStyles = (type: string) => {
    switch (type) {
      case "urgent":
      case "important":
        return {
          container: "bg-red-50/50 border-red-200/60",
          text: "text-slate-900",
          content: "text-slate-700",
          meta: "text-slate-500",
          badge:
            "bg-gradient-to-br from-red-300 to-pink-400 text-white font-bold shadow-sm",
          icon: "bg-gradient-to-br from-red-300 to-pink-400",
        };
      case "academic":
        return {
          container: "bg-blue-50/50 border-blue-200/60",
          text: "text-slate-900",
          content: "text-slate-700",
          meta: "text-slate-500",
          badge:
            "bg-gradient-to-br from-blue-300 to-indigo-400 text-white font-bold shadow-sm",
          icon: "bg-gradient-to-br from-blue-300 to-indigo-400",
        };
      case "info":
      case "general":
        return {
          container: "bg-emerald-50/50 border-emerald-200/60",
          text: "text-slate-900",
          content: "text-slate-700",
          meta: "text-slate-500",
          badge:
            "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white font-bold shadow-sm",
          icon: "bg-gradient-to-br from-emerald-300 to-emerald-400",
        };
      default:
        return {
          container: "bg-slate-50/50 border-slate-200/60",
          text: "text-slate-900",
          content: "text-slate-700",
          meta: "text-slate-500",
          badge:
            "bg-gradient-to-br from-slate-300 to-slate-400 text-white font-bold shadow-sm",
          icon: "bg-gradient-to-br from-slate-300 to-slate-400",
        };
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  // Quick action handlers
  const handleViewAttendance = () => {
    navigate("/teacher/attendance");
  };

  const handleManageGrades = () => {
    navigate("/teacher/exams-grades");
  };

  const handleCourseMaterials = () => {
    navigate("/teacher/courses");
  };

  const handleSchedule = () => {
    navigate("/teacher/schedule");
  };

  const handleCreateAnnouncement = () => {
    navigate("/teacher/announcements");
  };

  const handleViewAllNotices = () => {
    navigate("/teacher/announcements");
  };

  // Helper to get department name
  const getDepartmentName = () => {
    if (!teacher?.department) return "";
    const dept = teacher.department as any;
    if (typeof dept === "object" && dept !== null && dept.name) {
      return dept.name;
    }
    return String(dept);
  };

  // Get classes directly from teacher object
  const teacherClasses = teacher?.classes || teacher?.currentClasses || [];

  const totalClasses = teacherClasses.length;

  // Calculate total students from class enrollment data
  const totalStudents = teacherClasses.reduce(
    (total: number, classAssignment: any) => {
      return total + (classAssignment.classId?.currentEnrollment || 0);
    },
    0
  );

  // Calculate stats with proper null checks
  const todayPresent = attendanceData?.data?.stats?.totalPresent || 0;
  const attendanceRate = attendanceData?.data?.stats?.attendanceRate || 0;

  //Date
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      {/* Header */}

      <div className="mb-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-slate-200/60">
        <div className="text-right">
          <p className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {currentDate}
          </p>
        </div>
        <h1 className="text-3xl font-bold text-slate-900">
          Welcome back,{" "}
          {teacher?.userInfo?.firstName ||
            (teacher?.userId as any)?.firstName ||
            "Teacher"}
          !
        </h1>
        <p className="text-slate-600 mt-2 font-medium">
          {getDepartmentName() && teacher?.designation
            ? `${getDepartmentName()} • ${teacher.designation}`
            : getDepartmentName() || teacher?.designation || "Faculty Member"}
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* My Classes Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-600">My Classes</p>
              <p className="text-2xl font-bold text-slate-900">
                {totalClasses.toString()}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-md">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Total Students Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-600">Total Students</p>
              <p className="text-2xl font-bold text-slate-900">
                {totalStudents.toString()}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Today Present Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-600">Today Present</p>
              <p className="text-2xl font-bold text-slate-900">
                {todayPresent.toString()}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center shadow-md">
              <Calendar className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Attendance Rate Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-600">
                Attendance Rate
              </p>
              <p className="text-2xl font-bold text-slate-900">{`${attendanceRate.toFixed(
                1
              )}%`}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-md">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Classes */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-md">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">My Classes</h2>
            </div>
          </div>
          {teacherLoading ? (
            <div className="flex justify-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-500"></div>
            </div>
          ) : teacherError ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2 font-bold">
                Error loading classes
              </div>
              <p className="text-sm text-slate-500 font-medium">
                Unable to load class information
              </p>
            </div>
          ) : teacherClasses.length > 0 ? (
            <div className="space-y-3">
              {teacherClasses
                .slice(0, 5)
                .map((classAssignment: any, index: number) => {
                  // Extract class info from the assignment
                  const cls = classAssignment.classId;
                  const isPopulated = typeof cls === "object";
                  const classInfo = isPopulated
                    ? cls
                    : { _id: cls, name: "Unknown Class" };

                  return (
                    <div
                      key={classInfo._id || `class-${index}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all duration-300 hover:scale-[1.02] border border-slate-200/60"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-900">
                            {classInfo.name || "Unnamed Class"}
                          </h3>
                          {classAssignment?.isClassTeacher && (
                            <span className="px-2 py-1 text-xs bg-gradient-to-br from-blue-300 to-indigo-400 text-white rounded-xl font-bold shadow-sm">
                              Class Teacher
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 font-semibold">
                          {classInfo.students?.length || 0} students • Room{" "}
                          {typeof classInfo.room === "object" && classInfo.room
                            ? `${classInfo.room.number || ""} ${
                                classInfo.room.building || ""
                              } ${
                                classInfo.room.floor
                                  ? `Floor ${classInfo.room.floor}`
                                  : ""
                              }`.trim() || "TBA"
                            : classInfo.room || "TBA"}
                        </p>
                        {classInfo.grade && (
                          <p className="text-xs text-slate-500 font-medium">
                            Grade: {classInfo.grade}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900">
                          {classInfo.capacity || "N/A"}
                        </p>
                        <p className="text-xs text-slate-500 font-semibold">
                          Capacity
                        </p>
                      </div>
                    </div>
                  );
                })}
              {teacherClasses.length > 5 && (
                <div className="text-center pt-2">
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-bold hover:scale-105 transition-all">
                    View all {teacherClasses.length} classes
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
              <h3 className="mt-2 text-sm font-bold text-slate-900">
                No classes assigned
              </h3>
              <p className="mt-1 text-sm text-slate-500 font-medium">
                Contact your administrator to get assigned to classes.
              </p>
            </div>
          )}
        </div>

        {/* Notices & Announcements */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center shadow-md">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">
                Notices & Announcements
              </h2>
            </div>
            <button
              onClick={handleCreateAnnouncement}
              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-300 to-indigo-400 text-white rounded-xl text-sm font-bold hover:shadow-md hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-4 w-4" />
              Create
            </button>
          </div>

          {noticesLoading ? (
            <div className="flex justify-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-indigo-500"></div>
            </div>
          ) : notices.length > 0 ? (
            <div className="space-y-3">
              {notices.slice(0, 3).map((notice: Notice) => {
                const styles = getNoticeStyles(notice.type);
                return (
                  <div
                    key={notice._id}
                    className={`flex items-start p-4 border rounded-xl ${
                      styles.container
                    } ${
                      !notice.isRead ? "border-l-4 border-l-blue-400" : ""
                    } hover:shadow-md transition-all duration-300`}
                  >
                    <div className="mr-3 mt-0.5 flex-shrink-0">
                      <div
                        className={`w-8 h-8 rounded-lg ${styles.icon} flex items-center justify-center shadow-md`}
                      >
                        {getNoticeIcon(notice.type)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-bold ${styles.text}`}>
                            {notice.title}
                          </h3>
                          {!notice.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full shadow-sm"></div>
                          )}
                        </div>
                        <span
                          className={`text-xs px-3 py-1 rounded-xl ${styles.badge}`}
                        >
                          {notice.type.charAt(0).toUpperCase() +
                            notice.type.slice(1)}
                        </span>
                      </div>
                      <p
                        className={`text-sm mb-2 ${styles.content} line-clamp-2 font-medium`}
                      >
                        {notice.content}
                      </p>
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs ${styles.meta} font-semibold`}
                        >
                          From: {notice.author?.name || "Unknown"}
                        </span>
                        <span
                          className={`text-xs ${styles.meta} font-semibold`}
                        >
                          {formatTimeAgo(notice.publishDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* View All Button */}
              <div className="text-center pt-2">
                <button
                  onClick={handleViewAllNotices}
                  className="text-blue-600 hover:text-blue-700 text-sm font-bold hover:scale-105 transition-all"
                >
                  View all notices
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Bell className="h-8 w-8 text-white" />
              </div>
              <h3 className="mt-2 text-sm font-bold text-slate-900">
                No notices yet
              </h3>
              <p className="mt-1 text-sm text-slate-500 font-medium">
                New notices and announcements will appear here.
              </p>
              <button
                onClick={handleCreateAnnouncement}
                className="mt-3 inline-flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-blue-300 to-indigo-400 text-white rounded-xl text-sm font-bold hover:shadow-md hover:scale-105 transition-all duration-300"
              >
                <Plus className="h-4 w-4" />
                Create Announcement
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={handleViewAttendance}
            className="flex items-center justify-center p-4 bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-all">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">View Attendance</span>
          </button>
          <button
            onClick={handleManageGrades}
            className="flex items-center justify-center p-4 bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-all">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Manage Grades</span>
          </button>
          <button
            onClick={handleCourseMaterials}
            className="flex items-center justify-center p-4 bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-all">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Course Materials</span>
          </button>
          <button
            onClick={handleSchedule}
            className="flex items-center justify-center p-4 bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-all">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Schedule</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
